/*
 * ===============================================================================================================
 *                                Copyright 2023, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip, Typography, TableCellProps } from '@mui/material';
import { LuiMaterialTableHeadCellStyles } from '@jda/lui-common-component-library-mui5';
import { MRT_Row, MRT_RowData } from 'material-react-table';
import { fetchAssignedUsers } from '../../store/user/users';
import {
  selectPaginationDetails,
  selectUsers,
  selectUsersError,
  selectUsersLoading
} from '../../store/user/assignedUsers/assignedUsersSelector';
import { selectAppLocation } from '../../store/appLocation/appLocationSelector';
import { IRoleSelected } from '../../models/Roles/RolesContracts';
import CustomTablePagination from '../../shared/CustomTablePagination';
import NoItems from '../../shared/NoItems';
import Loader from '../../shared/Loader';
import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import useWindowDimensions from '../../utility/useWindowDimensions';
import { NO_RESULT_FOUND, PLEASE_TRY_A_DIFFERENT_ROLE } from '../../container/constants';
import { ROWS_PER_PAGE_LABEL, LABEL, assignedUsersDefaultSearchParams } from './constants';

const AssignedUsers = ({ selectedItem }: IRoleSelected) => {
  const assignedUsers = useSelector(selectUsers);
  const selectedPaginationDetails = useSelector(selectPaginationDetails);
  const loading = useSelector(selectUsersLoading);
  const error = useSelector(selectUsersError);
  const appLocation = useSelector(selectAppLocation);
  const dispatch = useDispatch();
  const { height } = useWindowDimensions();

  const getAllUsers = useCallback(() => {
    fetchAssignedUsers(dispatch, selectedItem.id, assignedUsersDefaultSearchParams, appLocation?.name);
  }, [dispatch, selectedItem.id, appLocation]);

  useEffect(() => {
    getAllUsers();
  }, [getAllUsers, selectedItem.id]);

  const onChangePageOptions = (itemsPerPage: number, page: number) => {
    const search = { ...assignedUsersDefaultSearchParams, 'page-number': page, 'page-size': itemsPerPage };
    fetchAssignedUsers(dispatch, selectedItem.id, search, appLocation?.name);
  };

  const columns = [
    {
      header: LABEL.USER_NAME,
      accessorKey: 'userName',
      muiTableHeadCellProps: ({ column, table }) =>
                    ({
                      sx: theme => ({
                        ...LuiMaterialTableHeadCellStyles({
                          column,
                          theme,
                          table,
                          styling: 'standard',
                          virtualizationEnabled: false
                        }),
                        width: '33%'
                      })
                    } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
        <Tooltip
          arrow
          title={row.original.userName}
          placement="bottom"
          disableHoverListener={!row.original.userName}
          disableFocusListener={!row.original.userName}
        >
          <Typography variant="body1" color="textPrimary" noWrap>
            {row.original.userName}
          </Typography>
        </Tooltip>
      )
    },
    {
      header: LABEL.NAME,
      accessorKey: 'displayName',
      muiTableHeadCellProps: ({ column, table }) =>
                    ({
                      sx: theme => ({
                        ...LuiMaterialTableHeadCellStyles({
                          column,
                          theme,
                          table,
                          styling: 'standard',
                          virtualizationEnabled: false
                        }),
                        width: '33%'
                      })
                    } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
        <Tooltip
          arrow
          title={row.original.displayName || LABEL.NO_VALUE}
          placement="bottom"
          disableHoverListener={!row.original.displayName}
          disableFocusListener={!row.original.displayName}
        >
          <Typography variant="body1" color="textPrimary" noWrap>
            {row.original.displayName || LABEL.NO_VALUE}
          </Typography>
        </Tooltip>
      )
    },
    {
      header: LABEL.EMAIL,
      accessorKey: 'email',
      muiTableHeadCellProps: ({ column, table }) =>
                    ({
                      sx: theme => ({
                        ...LuiMaterialTableHeadCellStyles({
                          column,
                          theme,
                          table,
                          styling: 'standard',
                          virtualizationEnabled: false
                        }),
                        width: '33%'
                      })
                    } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
        <Tooltip
          arrow
          title={row.original.email || LABEL.NO_VALUE}
          placement="bottom"
          disableHoverListener={!row.original.email}
          disableFocusListener={!row.original.email}
        >
          <Typography variant="body1" color="textPrimary" noWrap>
            {row.original.email || LABEL.NO_VALUE}
          </Typography>
        </Tooltip>
      )
    }
  ];

  if (loading) {
    return <Loader showLoader={loading} isCenter />;
  }

  if (error) {
    return <ErrorMessage message={error.message} data-testid="assigned-users-error-section" />;
  }

  if (!assignedUsers || assignedUsers.length === 0) {
    return <NoItems title={NO_RESULT_FOUND} subHeading={PLEASE_TRY_A_DIFFERENT_ROLE} />;
  }

  return (
    <CustomTablePagination
      columns={columns}
      data={assignedUsers}
      data-testid="user-by-role-table"
      rowsPerPageOptions={[20, 50, 100]}
      totalCount={selectedPaginationDetails.count}
      labelRowsPerPage={ROWS_PER_PAGE_LABEL}
      maxTableBodyHeight={height - 420}
      onChangePageOptions={onChangePageOptions}
      currentPageNumber={selectedPaginationDetails.pageNumber}
      currentItemsPerPage={selectedPaginationDetails.pageSize}
    />
  );
};

export default AssignedUsers;
